import dayjs from "dayjs";

import Button from "@/components/Button";
import { formatTime } from "@/pages/Reservation/components/ReservationTab/BatchReservationTab/constants";
import type {
  BatchPreviewResult,
  ConflictReservation,
} from "@/types/batchReservation";

/** 충돌 예약 한 건을 툴팁 본문 한 덩어리로 렌더링한다. */
const conflictSummary = (conflict: ConflictReservation) =>
  [
    `[예약자] ${conflict.userName || "알 수 없음"}`,
    `[공동체] ${conflict.cmtNm || "없음"}`,
    `[사용 목적] ${conflict.useCnts || "없음"}`,
    `[예약 시간] ${
      conflict.startTime && conflict.endTime
        ? `${formatTime(conflict.startTime)} ~ ${formatTime(conflict.endTime)}`
        : "없음"
    }`,
  ].join("\n");

const ConflictInfoCell = ({
  conflicts,
  isTopRow,
}: {
  conflicts: ConflictReservation[];
  isTopRow: boolean;
}) => {
  const [first] = conflicts;
  const extraCount = conflicts.length - 1;

  return (
    <span className="inline-flex items-center gap-1">
      <span>
        {first.userName || "알 수 없음"}
        {extraCount > 0 && ` 외 ${extraCount}건`}
      </span>
      <span className="group relative cursor-pointer rounded bg-gray-200 px-1 py-0.5 text-xs text-gray-600 hover:bg-gray-300">
        상세
        <span
          className={`pointer-events-none absolute left-1/2 z-50 hidden w-max max-w-[280px] -translate-x-1/2 whitespace-pre-wrap rounded bg-gray-800 px-3 py-2 text-xs leading-5 text-white shadow-lg group-hover:block ${
            isTopRow ? "top-full mt-1" : "bottom-full mb-1"
          }`}
        >
          {conflicts.map(conflictSummary).join("\n---\n")}
        </span>
      </span>
    </span>
  );
};

interface BatchPreviewTableProps {
  previewResults: BatchPreviewResult[];
  includeDates: Set<string>;
  conflictCount: number;
  isLoading: boolean;
  onToggleInclude: (date: string) => void;
  onToggleSelectAll: () => void;
  onOpenConflictModal: () => void;
  onSubmit: () => void;
}

const BatchPreviewTable = ({
  previewResults,
  includeDates,
  conflictCount,
  isLoading,
  onToggleInclude,
  onToggleSelectAll,
  onOpenConflictModal,
  onSubmit,
}: BatchPreviewTableProps) => {
  const allAvailableDates = previewResults
    .filter(result => result.available)
    .map(result => result.date);

  const isAllSelected =
    allAvailableDates.length > 0 &&
    allAvailableDates.every(date => includeDates.has(date));

  const includeCount = includeDates.size;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-small font-medium text-primary">
          예약 가능 날짜 ({includeCount} / {previewResults.length}일 예약 예정)
        </p>
        {conflictCount > 0 && (
          <Button
            variant="outlined"
            onClick={onOpenConflictModal}
            className="text-small"
          >
            충돌 {conflictCount}건 관리
          </Button>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full text-small">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="w-12 px-3 py-2 text-center font-medium text-gray-dark">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={onToggleSelectAll}
                  className="accent-primary"
                />
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-dark">
                날짜
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-dark">
                상태
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-dark">
                불가 이유
              </th>
            </tr>
          </thead>
          <tbody>
            {previewResults.map((result, idx) => {
              const isIncluded = includeDates.has(result.date);
              const isUnavailable = !result.available;
              const isTopRow = idx < 2;

              return (
                <tr
                  key={result.date}
                  className={`border-b border-gray-200 last:border-0 ${
                    isUnavailable
                      ? "bg-red-50 text-gray-400"
                      : isIncluded
                        ? "bg-white"
                        : "bg-gray-50"
                  }`}
                >
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={isIncluded}
                      disabled={isUnavailable}
                      onChange={() => onToggleInclude(result.date)}
                      className="accent-primary"
                    />
                  </td>
                  <td className="px-3 py-2">
                    {dayjs(result.date, "YYYYMMDD").format("YYYY.MM.DD (ddd)")}
                  </td>
                  <td className="px-3 py-2">
                    {result.available ? (
                      <span className="text-green-600">예약 가능</span>
                    ) : (
                      <span className="text-red-500">불가</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-500">
                    {result.conflicts.length > 0 ? (
                      <ConflictInfoCell
                        conflicts={result.conflicts}
                        isTopRow={isTopRow}
                      />
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end pb-4 pt-2">
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={isLoading || includeCount === 0}
          className="text-small"
        >
          {isLoading ? "등록 중..." : `일괄 예약 등록 (${includeCount}건)`}
        </Button>
      </div>
    </div>
  );
};

export default BatchPreviewTable;
